import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section } from "../../components/Common";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Sparkles, FileText } from "lucide-react";

export default function StudentInsights() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loadingKind, setLoadingKind] = useState(null);

  const targetId = user.role === "parent" ? user.parent_of : user.id;

  const load = async () => {
    const { data } = await api.get("/ai/history");
    setHistory(data);
  };
  useEffect(() => { load(); }, []);

  const generate = async (kind) => {
    if (!targetId) {
      toast.error("No student linked to this account.");
      return;
    }
    setLoadingKind(kind);
    try {
      const url = kind === "report_card" ? "/ai/report-card" : "/ai/insights";
      await api.post(url, { student_id: targetId });
      toast.success("Generated by Claude");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoadingKind(null);
    }
  };

  return (
    <div>
      <PageHeader title="AI Insights & Reports" subtitle="Powered by Claude — personalised analysis of your performance." />

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <button onClick={() => generate("insight")} disabled={loadingKind === "insight"} data-testid="gen-insight"
          className="text-left p-6 rounded-xl bg-gradient-to-br from-blue-600 to-blue-700 text-white ym-card-hover disabled:opacity-70">
          <Sparkles className="h-6 w-6" />
          <h3 className="mt-3 font-heading font-bold text-lg">Generate insights</h3>
          <p className="text-sm text-blue-100 mt-1">Personal study tips and motivation.</p>
          <span className="mt-4 inline-block text-sm font-semibold underline">{loadingKind === "insight" ? "Thinking…" : "Generate →"}</span>
        </button>
        <button onClick={() => generate("report_card")} disabled={loadingKind === "report_card"} data-testid="gen-report"
          className="text-left p-6 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white ym-card-hover disabled:opacity-70">
          <FileText className="h-6 w-6" />
          <h3 className="mt-3 font-heading font-bold text-lg">Generate report card</h3>
          <p className="text-sm text-emerald-100 mt-1">Formal summary for parents.</p>
          <span className="mt-4 inline-block text-sm font-semibold underline">{loadingKind === "report_card" ? "Writing…" : "Generate →"}</span>
        </button>
      </div>

      <Section title="History">
        <div className="space-y-4">
          {history.map((h) => (
            <div key={h.id} className="border-l-4 border-blue-500 bg-blue-50/30 rounded-r-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-blue-600">
                  {h.kind === "report_card" ? "Report card" : "Insights"}
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-400">{new Date(h.created_at).toLocaleString()}</span>
                  {h.kind === "report_card" && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/ai/report-card/${h.id}/pdf?auth=${encodeURIComponent(localStorage.getItem("ym_access_token") || "")}`}
                      target="_blank" rel="noreferrer"
                      data-testid={`download-pdf-${h.id}`}
                      className="text-xs font-semibold text-blue-600 hover:underline">
                      Download PDF →
                    </a>
                  )}
                </div>
              </div>
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{h.text}</pre>
            </div>
          ))}
          {history.length === 0 && <p className="text-sm text-slate-500">No insights yet — generate your first one above.</p>}
        </div>
      </Section>
    </div>
  );
}
